import Phaser from "phaser";

import type { FoodRuntime } from "../application/food-runtime";
import type { BotRuntime } from "../application/bot-runtime";
import type { FirstRunHint } from "../application/first-run-hint";
import type { PlayerRuntime } from "../application/player-runtime";
import type { TailClipRuntime } from "../application/tail-clip-runtime";
import type { BonusRuntime } from "../application/bonus-runtime";
import {
  formatCoinRewardFeedback,
  type CoinRewardFeedbackPort,
} from "../application/coin-reward-feedback";
import { gameplayConfig } from "../config/gameplay-config";
import type { MotionProfile } from "../config/motion-config";
import {
  getDefaultArenaBackgroundId,
  type ArenaBackgroundId,
} from "../config/arena-background-config";
import type { Vector2 } from "../simulation/player-movement";
import type { SeasonalChallengeDescriptor } from "../config/seasonal-challenge-config";
import {
  calculateArenaCameraTarget,
  type CameraViewport,
  smoothArenaCameraHeading,
  smoothArenaCameraTarget,
} from "./arena-camera";
import { drawArenaBackground } from "./arena-renderer";
import { FoodRenderer } from "./food-renderer";
import { BotRenderer } from "./bot-renderer";
import { drawPlayer } from "./player-renderer";
import { getSnakeChompStartedAtMs } from "./snake-motion";
import { TailClipRenderer } from "./tail-clip-renderer";
import { drawBonuses, getBonusPickupFeedback } from "./bonus-renderer";
import {
  VirtualJoystickInput,
  insetJoystickBounds,
  type JoystickBounds,
  type JoystickPointerSample,
  type JoystickSafeAreaInsets,
  type PointerKind,
} from "./virtual-joystick-input";
import type { PlayerBodyTrailState } from "../simulation/player-body-trail";
import {
  clampPlayerStatusX,
  formatPlayerBadge,
  formatPlayerFeedback,
  getPlayerStatusLayout,
} from "../application/player-status";
import { interpolateRenderFrame } from "./render-interpolation";
import { drawCoinRewardSparks } from "./coin-reward-feedback-renderer";

const emptyPlayerBody: PlayerBodyTrailState = {
  path: [],
  rippleStartedAtMs: null,
  segments: [],
};

export class BootScene extends Phaser.Scene {
  private readonly virtualJoystick: VirtualJoystickInput;
  private arenaGraphics: Phaser.GameObjects.Graphics | undefined;
  private botRenderer: BotRenderer | undefined;
  private bonusGraphics: Phaser.GameObjects.Graphics | undefined;
  private cameraHeadingRadians: number | undefined;
  private cameraPlayerAlive: boolean | undefined;
  private cameraTarget: Vector2 | undefined;
  private cameraRunId: number | undefined;
  private cameraViewport: CameraViewport | undefined;
  private foodRenderer: FoodRenderer | undefined;
  private frameCount = 0;
  private inputListenerCount = 0;
  private playerGraphics: Phaser.GameObjects.Graphics | undefined;
  private playerBadge: Phaser.GameObjects.Text | undefined;
  private playerFeedback: Phaser.GameObjects.Text | undefined;
  private coinFeedback: Phaser.GameObjects.Text | undefined;
  private pointerGraphics: Phaser.GameObjects.Graphics | undefined;
  private status: Phaser.GameObjects.Text | undefined;
  private tailClipRenderer: TailClipRenderer | undefined;
  private title: Phaser.GameObjects.Text | undefined;
  private renderActive = true;

  public constructor(
    private readonly runtime: PlayerRuntime,
    private readonly foodRuntime: FoodRuntime,
    private readonly botRuntime: BotRuntime,
    private readonly firstRunHint: FirstRunHint,
    private readonly getMotionProfile: () => MotionProfile,
    private readonly bonusRuntime: BonusRuntime,
    private readonly tailClipRuntime?: TailClipRuntime,
    private readonly coinRewardFeedback?: CoinRewardFeedbackPort,
    private readonly getArenaBackgroundId: () => ArenaBackgroundId = getDefaultArenaBackgroundId,
    private readonly getSafeAreaInsets: () => JoystickSafeAreaInsets = () => ({
      bottom: 0,
      left: 0,
      right: 0,
      top: 0,
    }),
    private readonly getSeasonalArenaDescriptor: () => SeasonalChallengeDescriptor | null = () =>
      null,
    private readonly getHighContrastPreference: () => boolean = () => false,
  ) {
    super("snakely");
    this.virtualJoystick = new VirtualJoystickInput(
      gameplayConfig.virtualJoystick,
      {
        capture: (captureId) => {
          this.capturePointer(captureId);
        },
        releaseCapture: (captureId) => {
          this.releasePointerCapture(captureId);
        },
        releaseSteering: () => {
          this.runtime.releaseSteering();
        },
        steer: (direction) => {
          this.runtime.steerDirection(direction);
        },
      },
    );
  }

  public create(): void {
    const centerX = this.scale.width / 2;
    const centerY = this.scale.height / 2;
    this.title = this.add.text(centerX, centerY - 38, "Snakely", {
      color: "#f7fff7",
      fontFamily: "Arial, sans-serif",
      fontSize: "88px",
      fontStyle: "bold",
    });
    this.status = this.add.text(centerX, centerY + 64, "Phaser is ready", {
      color: "#b7f7d0",
      fontFamily: "Arial, sans-serif",
      fontSize: "30px",
    });
    const arenaGraphics = this.add.graphics();
    this.arenaGraphics = arenaGraphics;
    this.foodRenderer = new FoodRenderer(this.add.graphics());
    this.botRenderer = new BotRenderer(
      this.add.graphics(),
      (x, y, text, style) => this.add.text(x, y, text, style),
      gameplayConfig.arena,
    );
    this.playerGraphics = this.add.graphics();
    this.bonusGraphics = this.add.graphics();
    this.tailClipRenderer = new TailClipRenderer(
      this.add.graphics(),
      this.add
        .text(0, 0, "", {
          color: "#ffe066",
          fontFamily: "Arial, sans-serif",
          fontSize: "22px",
          fontStyle: "bold",
          stroke: "#102a43",
          strokeThickness: 5,
        })
        .setOrigin(0.5, 1),
    );
    this.pointerGraphics = this.add.graphics().setScrollFactor(0).setDepth(100);
    this.playerBadge = this.add
      .text(0, 0, "", {
        backgroundColor: "#102a43",
        color: "#f7fff7",
        fontFamily: "Arial, sans-serif",
        fontSize: "18px",
        fontStyle: "bold",
        padding: { bottom: 5, left: 9, right: 9, top: 5 },
        stroke: "#102a43",
        strokeThickness: 3,
      })
      .setOrigin(0.5, 1);
    this.playerFeedback = this.add
      .text(0, 0, "", {
        color: "#ffe066",
        fontFamily: "Arial, sans-serif",
        fontSize: "24px",
        fontStyle: "bold",
        stroke: "#102a43",
        strokeThickness: 5,
      })
      .setOrigin(0.5, 1);
    this.coinFeedback = this.add
      .text(0, 0, "", {
        color: "#ffe066",
        fontFamily: "Arial, sans-serif",
        fontSize: "26px",
        fontStyle: "bold",
        stroke: "#102a43",
        strokeThickness: 6,
      })
      .setOrigin(0.5, 1);
    this.input.on("pointerdown", this.handlePointerDown);
    this.input.on("pointermove", this.handlePointerMove);
    this.input.on("pointerup", this.handlePointerUp);
    this.input.on("pointerupoutside", this.handlePointerUp);
    this.game.canvas.addEventListener(
      "pointercancel",
      this.handlePointerCancel,
    );
    this.game.canvas.addEventListener(
      "lostpointercapture",
      this.handleLostPointerCapture,
    );
    this.inputListenerCount = 6;
    this.events.once("shutdown", this.handleShutdown);

    this.cameras.main.setBackgroundColor("#102a43");
    this.cameras.main.setBounds(
      0,
      0,
      gameplayConfig.arena.width,
      gameplayConfig.arena.height,
    );
    this.game.canvas.setAttribute(
      "aria-label",
      "Snakely game canvas. Phaser booted.",
    );
    this.drawSelectedArenaBackground(arenaGraphics);
    this.title.setOrigin(0.5);
    this.status.setOrigin(0.5);
  }

  public override update(time: number, delta: number): void {
    this.frameCount += 1;
    this.runtime.sampleFrame(time);
    const snapshot = this.runtime.getSnapshot();
    const graphics = this.playerGraphics;
    const arenaGraphics = this.arenaGraphics;
    const title = this.title;
    const status = this.status;
    if (
      arenaGraphics === undefined ||
      this.bonusGraphics === undefined ||
      this.foodRenderer === undefined ||
      graphics === undefined ||
      this.playerBadge === undefined ||
      this.playerFeedback === undefined ||
      this.coinFeedback === undefined ||
      this.pointerGraphics === undefined ||
      title === undefined ||
      status === undefined
    ) {
      throw new Error("Snakely scene updated before creation");
    }
    const currentPlayer = snapshot.player;
    const startingRun = this.cameraRunId !== snapshot.runId;
    const respawned =
      this.cameraPlayerAlive === false && currentPlayer?.alive === true;
    const resized =
      this.cameraViewport?.width !== this.cameras.main.width ||
      this.cameraViewport.height !== this.cameras.main.height;
    if (currentPlayer !== null && (startingRun || respawned || resized)) {
      this.runtime.resetRenderInterpolation();
      this.botRuntime.resetRenderInterpolation();
    }
    this.cameraPlayerAlive = currentPlayer?.alive;
    if (currentPlayer !== null && startingRun) {
      this.drawSelectedArenaBackground(arenaGraphics);
    }
    const renderFrame = interpolateRenderFrame(
      this.runtime.getRenderSnapshot(),
      this.botRuntime.getRenderSnapshot(),
    );
    const player = renderFrame.player?.player ?? null;
    const body = renderFrame.player?.body ?? emptyPlayerBody;
    const bonusSnapshot = this.bonusRuntime.getSnapshot();
    const motionProfile = this.getMotionProfile();
    drawBonuses(
      this.bonusGraphics,
      bonusSnapshot,
      renderFrame.simulationTimeMs,
      motionProfile,
    );
    const tailClipSnapshot = this.tailClipRuntime?.getSnapshot() ?? null;
    this.foodRenderer.render({
      feedback: snapshot.feedback,
      foods: this.foodRuntime.getSnapshot().foods,
      motionProfile,
      player,
      runId: snapshot.runId,
      simulationTimeMs: renderFrame.simulationTimeMs,
    });
    this.botRenderer?.render(
      renderFrame.bots,
      player,
      renderFrame.simulationTimeMs,
      motionProfile,
      tailClipSnapshot?.lastClip ?? null,
    );
    if (tailClipSnapshot !== null) {
      this.tailClipRenderer?.render(
        tailClipSnapshot,
        renderFrame.simulationTimeMs,
        motionProfile,
        player?.position ?? null,
      );
    }
    const gameplayVisible = player !== null;
    title.setVisible(!gameplayVisible);
    status.setVisible(!gameplayVisible);
    if (player === null) {
      graphics.clear();
      this.clearPlayerMotionAttributes();
      this.hidePlayerStatus();
      this.virtualJoystick.cancel();
      this.cameraHeadingRadians = undefined;
      this.cameraPlayerAlive = undefined;
      this.cameraTarget = undefined;
      this.cameraRunId = undefined;
      this.cameraViewport = undefined;
      this.cameras.main.centerOn(
        this.cameras.main.width / 2,
        this.cameras.main.height / 2,
      );
      return;
    }

    if (resized) {
      this.virtualJoystick.cancel();
    }
    const cameraElapsedMilliseconds =
      snapshot.paused || snapshot.backgrounded ? 0 : delta;
    this.cameraHeadingRadians =
      this.cameraHeadingRadians === undefined ||
      startingRun ||
      respawned ||
      resized
        ? player.directionRadians
        : smoothArenaCameraHeading(
            this.cameraHeadingRadians,
            player.directionRadians,
            motionProfile.cameraFollowRatePerSecond,
            cameraElapsedMilliseconds,
          );
    const desiredCameraTarget = calculateArenaCameraTarget(
      {
        directionRadians: this.cameraHeadingRadians,
        position: player.position,
      },
      {
        ...gameplayConfig.arena,
        cameraLookAheadDistance: motionProfile.cameraLookAheadDistance,
      },
      this.cameras.main,
    );
    this.cameraRunId = snapshot.runId;
    this.cameraViewport = {
      height: this.cameras.main.height,
      width: this.cameras.main.width,
    };
    this.cameraTarget =
      this.cameraTarget === undefined || startingRun || respawned || resized
        ? desiredCameraTarget
        : smoothArenaCameraTarget(
            this.cameraTarget,
            desiredCameraTarget,
            motionProfile.cameraFollowRatePerSecond,
            cameraElapsedMilliseconds,
          );
    this.cameras.main.centerOn(this.cameraTarget.x, this.cameraTarget.y);

    if (!player.alive) {
      graphics.clear();
      this.clearPlayerMotionAttributes();
      this.hidePlayerStatus();
      this.virtualJoystick.cancel();
      return;
    }
    if (snapshot.paused || snapshot.backgrounded) {
      this.virtualJoystick.cancel();
    }
    const playerFace = drawPlayer(
      graphics,
      player,
      body,
      renderFrame.simulationTimeMs,
      motionProfile,
      bonusSnapshot.turboActiveRemainingMs > 0,
      getSnakeChompStartedAtMs(
        "player",
        snapshot.feedback?.startedAtMs ?? null,
        tailClipSnapshot?.lastClip ?? null,
      ),
    );
    this.game.canvas.setAttribute(
      "data-player-motion",
      motionProfile.snakeMotionScale === 0 ? "static" : "active",
    );
    this.game.canvas.setAttribute(
      "data-player-eyes",
      playerFace.eyesClosed ? "closed" : "open",
    );
    this.game.canvas.setAttribute(
      "data-player-mouth",
      playerFace.mouthOpenScale > 0 ? "open" : "closed",
    );
    this.game.canvas.setAttribute(
      "data-player-tongue",
      playerFace.tongueLengthScale > 1 ? "flick" : "static",
    );
    const statusLayout = getPlayerStatusLayout(player);
    const badgeText = formatPlayerBadge(player.level);
    this.playerBadge.setText(badgeText);
    const badgeX = clampPlayerStatusX(
      statusLayout.badge.x,
      this.playerBadge.displayWidth / 2,
      this.cameras.main.worldView,
    );
    this.playerBadge.setPosition(badgeX, statusLayout.badge.y).setVisible(true);
    this.game.canvas.setAttribute("data-player-level", badgeText);
    const feedback = snapshot.feedback;
    const bonusFeedback = getBonusPickupFeedback(
      bonusSnapshot.lastEvent,
      renderFrame.simulationTimeMs,
      motionProfile,
    );
    this.playerFeedback
      .setPosition(statusLayout.feedback.x, statusLayout.feedback.y)
      .setText(
        bonusFeedback ??
          (feedback === null ? "" : formatPlayerFeedback(feedback)),
      )
      .setVisible(bonusFeedback !== null || feedback !== null);
    const coinFeedback =
      this.coinRewardFeedback?.getSnapshot().feedback ?? null;
    if (coinFeedback === null) {
      this.coinFeedback.setText("").setVisible(false);
    } else {
      this.coinFeedback
        .setPosition(coinFeedback.position.x, coinFeedback.position.y - 18)
        .setText(formatCoinRewardFeedback(coinFeedback))
        .setVisible(true);
    }
    drawCoinRewardSparks(
      this.bonusGraphics,
      coinFeedback,
      renderFrame.simulationTimeMs,
      motionProfile,
    );
    this.drawPointerFeedback();
  }

  public getStabilitySnapshot(): {
    readonly frameCount: number;
    readonly inputListenerCount: number;
    readonly renderActive: boolean;
    readonly timerCount: number;
    readonly transientEntityCount: number;
  } {
    const active = this.arenaGraphics !== undefined;
    return {
      frameCount: this.frameCount,
      inputListenerCount: this.inputListenerCount,
      renderActive: this.renderActive,
      timerCount: 0,
      transientEntityCount: active
        ? 15 + (this.botRenderer?.getActiveLabelCount() ?? 0)
        : 0,
    };
  }

  public prepareForRenderWake(): void {
    this.virtualJoystick.cancel();
    this.runtime.releaseSteering();
    this.runtime.resetAutomaticFrameTiming();
    this.runtime.resetRenderInterpolation();
    this.botRuntime.resetRenderInterpolation();
    this.pointerGraphics?.clear();
    this.cameraHeadingRadians = undefined;
    this.cameraPlayerAlive = undefined;
    this.cameraRunId = undefined;
    this.cameraTarget = undefined;
    this.cameraViewport = undefined;
  }

  public setRenderActive(active: boolean): void {
    this.renderActive = active;
    if (!active) {
      this.virtualJoystick.cancel();
      this.runtime.releaseSteering();
      this.pointerGraphics?.clear();
    }
  }

  private clearPlayerMotionAttributes(): void {
    for (const attribute of [
      "data-player-eyes",
      "data-player-mouth",
      "data-player-motion",
      "data-player-tongue",
    ]) {
      this.game.canvas.removeAttribute(attribute);
    }
  }

  private drawSelectedArenaBackground(
    graphics: Phaser.GameObjects.Graphics,
  ): void {
    const id = this.getArenaBackgroundId();
    const seasonal = this.getSeasonalArenaDescriptor();
    const reducedMotion = this.getMotionProfile().particleOpacity === 0;
    const highContrast = this.getHighContrastPreference();
    drawArenaBackground(
      graphics,
      gameplayConfig.arena,
      id,
      seasonal ?? undefined,
      { highContrast, reducedMotion },
    );
    this.game.canvas.setAttribute("data-arena-background-id", id);
    if (seasonal === null) {
      delete this.game.canvas.dataset.seasonalChallenge;
      delete this.game.canvas.dataset.seasonalContrast;
      delete this.game.canvas.dataset.seasonalMotion;
      this.game.canvas.setAttribute(
        "aria-label",
        "Snakely game canvas. Phaser booted.",
      );
      return;
    }
    this.game.canvas.dataset.seasonalChallenge = seasonal.slug;
    this.game.canvas.dataset.seasonalContrast = highContrast
      ? "high"
      : "standard";
    this.game.canvas.dataset.seasonalMotion = reducedMotion
      ? "reduced"
      : "standard";
    this.game.canvas.setAttribute(
      "aria-label",
      seasonal.accessibility.arenaLabel,
    );
  }

  private readonly handlePointerDown = (
    pointer: Phaser.Input.Pointer,
  ): void => {
    const snapshot = this.runtime.getSnapshot();
    if (
      snapshot.player === null ||
      !snapshot.player.alive ||
      snapshot.paused ||
      snapshot.backgrounded
    ) {
      return;
    }
    this.virtualJoystick.begin(
      "arena",
      this.pointerSample(pointer),
      this.joystickBounds(),
    );
  };

  private readonly handlePointerMove = (
    pointer: Phaser.Input.Pointer,
  ): void => {
    if (this.virtualJoystick.move(this.pointerSample(pointer)) === "steered") {
      this.firstRunHint.completeSteering();
    }
  };

  private readonly handlePointerUp = (pointer: Phaser.Input.Pointer): void => {
    this.virtualJoystick.end(pointer.id);
  };

  private readonly handlePointerCancel = (event: PointerEvent): void => {
    this.virtualJoystick.cancelCapture(event.pointerId);
  };

  private readonly handleLostPointerCapture = (event: PointerEvent): void => {
    this.virtualJoystick.cancelCapture(event.pointerId);
  };

  private readonly handleShutdown = (): void => {
    this.input.off("pointerdown", this.handlePointerDown);
    this.input.off("pointermove", this.handlePointerMove);
    this.input.off("pointerup", this.handlePointerUp);
    this.input.off("pointerupoutside", this.handlePointerUp);
    this.game.canvas.removeEventListener(
      "pointercancel",
      this.handlePointerCancel,
    );
    this.game.canvas.removeEventListener(
      "lostpointercapture",
      this.handleLostPointerCapture,
    );
    this.inputListenerCount = 0;
    this.virtualJoystick.cancel();
    this.arenaGraphics?.destroy();
    this.foodRenderer?.destroy();
    this.botRenderer?.destroy();
    this.playerGraphics?.destroy();
    this.bonusGraphics?.destroy();
    this.tailClipRenderer?.destroy();
    this.playerBadge?.destroy();
    this.playerFeedback?.destroy();
    this.coinFeedback?.destroy();
    this.pointerGraphics?.destroy();
    this.status?.destroy();
    this.title?.destroy();
    this.arenaGraphics = undefined;
    this.cameraHeadingRadians = undefined;
    this.cameraPlayerAlive = undefined;
    this.cameraRunId = undefined;
    this.cameraTarget = undefined;
    this.cameraViewport = undefined;
    this.foodRenderer = undefined;
    this.botRenderer = undefined;
    this.playerGraphics = undefined;
    this.bonusGraphics = undefined;
    this.tailClipRenderer = undefined;
    this.playerBadge = undefined;
    this.playerFeedback = undefined;
    this.coinFeedback = undefined;
    this.pointerGraphics = undefined;
    this.status = undefined;
    this.title = undefined;
  };

  private drawPointerFeedback(): void {
    const graphics = this.pointerGraphics;
    if (graphics === undefined) {
      return;
    }
    graphics.clear();
    const joystick = this.virtualJoystick.getSnapshot();
    if (joystick.center === null || joystick.knob === null) {
      return;
    }
    const visual = gameplayConfig.virtualJoystick.visual;
    graphics.fillStyle(0x102a43, visual.fillAlpha);
    graphics.fillCircle(
      joystick.center.x,
      joystick.center.y,
      gameplayConfig.virtualJoystick.ringRadius,
    );
    graphics.lineStyle(5, 0xf7fff7, visual.ringAlpha);
    graphics.strokeCircle(
      joystick.center.x,
      joystick.center.y,
      gameplayConfig.virtualJoystick.ringRadius,
    );
    graphics.lineStyle(8, 0xf7fff7, visual.connectorAlpha);
    graphics.lineBetween(
      joystick.center.x,
      joystick.center.y,
      joystick.knob.x,
      joystick.knob.y,
    );
    graphics.fillStyle(0xffe066, visual.knobAlpha);
    graphics.fillCircle(
      joystick.knob.x,
      joystick.knob.y,
      gameplayConfig.virtualJoystick.knobRadius,
    );
    graphics.lineStyle(4, 0x102a43, visual.outlineAlpha);
    graphics.strokeCircle(
      joystick.knob.x,
      joystick.knob.y,
      gameplayConfig.virtualJoystick.knobRadius,
    );
  }

  private hidePlayerStatus(): void {
    this.playerBadge?.setVisible(false);
    this.playerFeedback?.setVisible(false);
    this.coinFeedback?.setVisible(false);
    this.pointerGraphics?.clear();
  }

  private capturePointer(captureId: number): void {
    if (captureId > 0) {
      this.game.canvas.setPointerCapture(captureId);
    }
  }

  private joystickBounds(): JoystickBounds {
    return insetJoystickBounds(
      {
        bottom: this.cameras.main.height,
        left: 0,
        right: this.cameras.main.width,
        top: 0,
      },
      this.getSafeAreaInsets(),
    );
  }

  private pointerKind(pointer: Phaser.Input.Pointer): PointerKind {
    if (pointer.wasTouch) {
      return "touch";
    }
    const nativeEvent: unknown = pointer.event;
    if (
      typeof nativeEvent === "object" &&
      nativeEvent !== null &&
      "pointerType" in nativeEvent &&
      (nativeEvent.pointerType === "touch" || nativeEvent.pointerType === "pen")
    ) {
      return nativeEvent.pointerType;
    }
    return "mouse";
  }

  private pointerSample(pointer: Phaser.Input.Pointer): JoystickPointerSample {
    const nativeEvent: unknown = pointer.event;
    const captureId =
      typeof nativeEvent === "object" &&
      nativeEvent !== null &&
      "pointerId" in nativeEvent &&
      typeof nativeEvent.pointerId === "number"
        ? nativeEvent.pointerId
        : 0;
    return {
      captureId,
      id: pointer.id,
      kind: this.pointerKind(pointer),
      position: { x: pointer.x, y: pointer.y },
    };
  }

  private releasePointerCapture(captureId: number): void {
    if (this.game.canvas.hasPointerCapture(captureId)) {
      this.game.canvas.releasePointerCapture(captureId);
    }
  }
}
